<template>
  <div class="hospital-reception">
    <!-- 顶部筛选区域 -->
    <div class="filter-area">
      <el-input placeholder="请输入患者姓名/ID" v-model="searchText" style="width: 200px; margin-right: 10px;">
      </el-input>
      <el-select v-model="statusFilter" placeholder="接诊状态" style="width: 120px; margin-right: 10px;">
        <el-option label="全部" value=""></el-option>
        <el-option label="待接诊" value="waiting"></el-option>
        <el-option label="接诊中" value="inProgress"></el-option>
        <el-option label="已完成" value="completed"></el-option>
      </el-select>
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 左侧患者列表 -->
    <div class="main-content">
      <div class="patient-list">
        <h3>今日患者列表</h3>
        <el-table :data="filteredPatients" highlight-current-row @row-click="handleSelectPatient">
          <el-table-column prop="id" label="ID" width="70"></el-table-column>
          <el-table-column prop="name" label="姓名" width="80"></el-table-column>
          <el-table-column prop="gender" label="性别" width="60"></el-table-column>
          <el-table-column prop="age" label="年龄" width="60"></el-table-column>
          <el-table-column prop="registerTime" label="挂号时间" width="160"></el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 右侧患者详情 -->
      <div class="patient-details" v-if="selectedPatient">
        <div class="patient-header">
          <h2>患者详情</h2>
          <div class="patient-info">
            <span><strong>姓名:</strong> {{ selectedPatient.name }}</span>
            <span><strong>性别:</strong> {{ selectedPatient.gender }}</span>
            <span><strong>年龄:</strong> {{ selectedPatient.age }}</span>
            <span><strong>电话:</strong> {{ selectedPatient.phone }}</span>
          </div>
          <div class="action-buttons">
            <!-- 基本接诊按钮 -->
            <el-button type="primary" :disabled="selectedPatient.status !== 'waiting'" @click="startDiagnosis">
              开始接诊
            </el-button>
            <el-button type="success" :disabled="selectedPatient.status !== 'inProgress'" @click="completeDiagnosis">
              完成接诊
            </el-button>

            <!-- 添加新的功能按钮 -->
            <el-button type="primary" icon="el-icon-document-add" :disabled="selectedPatient.status !== 'inProgress'"
              @click="dialogExamOrder = true">
              开检查单
            </el-button>
            <el-button type="primary" icon="el-icon-medicine-box" :disabled="selectedPatient.status !== 'inProgress'"
              @click="dialogPrescription = true">
              开药方
            </el-button>
            <el-button type="primary" icon="el-icon-edit-outline" :disabled="selectedPatient.status !== 'inProgress'"
              @click="dialogTreatment = true">
              记录治疗
            </el-button>
            <el-button type="info" icon="el-icon-date" :disabled="selectedPatient.status !== 'inProgress'"
              @click="dialogFollowup = true">
              建议复诊
            </el-button>
          </div>
        </div>

        <!-- 患者详细信息标签页 -->
        <el-tabs v-model="activeTab" type="card">
          <!-- 挂号信息 -->
          <el-tab-pane label="挂号信息" name="registration">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="挂号ID">{{ selectedPatient.registration.id }}</el-descriptions-item>
              <el-descriptions-item label="挂号时间">{{ selectedPatient.registration.registerTime }}</el-descriptions-item>
              <el-descriptions-item label="科室">{{ selectedPatient.registration.department }}</el-descriptions-item>
              <el-descriptions-item label="医生">{{ selectedPatient.registration.doctor }}</el-descriptions-item>
              <el-descriptions-item label="挂号费用">{{ selectedPatient.registration.fee }}元</el-descriptions-item>
              <el-descriptions-item label="支付状态">
                <el-tag type="success" v-if="selectedPatient.registration.paid">已支付</el-tag>
                <el-tag type="danger" v-else>未支付</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="主诉" :span="2">{{ selectedPatient.registration.complaint
              }}</el-descriptions-item>
            </el-descriptions>

            <!-- 添加复诊建议显示 -->
            <div v-if="selectedPatient.followupRecommendation" class="followup-recommendation">
              <h4>复诊建议</h4>
              <el-alert type="info" :title="'建议复诊时间: ' + selectedPatient.followupRecommendation.date"
                :description="'复诊原因: ' + selectedPatient.followupRecommendation.reason" show-icon :closable="false">
              </el-alert>
            </div>
          </el-tab-pane>

          <!-- 检查单信息 -->
          <el-tab-pane label="检查单信息" name="examOrder">
            <div v-if="selectedPatient.examOrders && selectedPatient.examOrders.length > 0">
              <el-table :data="selectedPatient.examOrders" border>
                <el-table-column prop="id" label="检查单号" width="120"></el-table-column>
                <el-table-column prop="name" label="检查项目" width="180"></el-table-column>
                <el-table-column prop="department" label="检查科室" width="120"></el-table-column>
                <el-table-column prop="doctor" label="开单医生" width="100"></el-table-column>
                <el-table-column prop="status" label="状态" width="100">
                  <template slot-scope="scope">
                    <el-tag :type="getExamStatusType(scope.row.status)">
                      {{ scope.row.status }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="fee" label="费用(元)" width="100"></el-table-column>
                <el-table-column prop="time" label="申请时间" width="160"></el-table-column>
              </el-table>
            </div>
            <el-empty v-else description="暂无检查单信息"></el-empty>
          </el-tab-pane>

          <!-- 检查结果 -->
          <el-tab-pane label="检查结果" name="examResult">
            <div v-if="selectedPatient.examResults && selectedPatient.examResults.length > 0">
              <div v-for="(result, index) in selectedPatient.examResults" :key="index" class="exam-result-item">
                <h4>{{ result.name }} <el-tag size="small">{{ result.time }}</el-tag></h4>
                <div class="result-content">
                  <div class="result-text">
                    <p><strong>检查结论：</strong>{{ result.conclusion }}</p>
                    <p><strong>检查医生：</strong>{{ result.doctor }}</p>
                    <p><strong>检查描述：</strong>{{ result.description }}</p>
                  </div>
                  <div class="result-images" v-if="result.images && result.images.length > 0">
                    <h5>检查图片：</h5>
                    <div class="image-list">
                      <div v-for="(img, imgIndex) in result.images" :key="imgIndex" class="image-item">
                        <el-image :src="img.url" :preview-src-list="result.images.map(i => i.url)" fit="cover">
                        </el-image>
                        <div class="image-title">{{ img.title }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无检查结果"></el-empty>
          </el-tab-pane>

          <!-- 治疗情况 -->
          <el-tab-pane label="治疗情况" name="treatment">
            <div v-if="selectedPatient.treatments && selectedPatient.treatments.length > 0">
              <el-timeline>
                <el-timeline-item v-for="(treatment, index) in selectedPatient.treatments" :key="index"
                  :timestamp="treatment.time" :type="treatment.important ? 'primary' : 'info'">
                  <el-card>
                    <h4>{{ treatment.title }}</h4>
                    <p>治疗医生: {{ treatment.doctor }}</p>
                    <p>治疗描述: {{ treatment.description }}</p>
                    <p>用药情况: {{ treatment.medication }}</p>
                    <p>治疗效果: {{ treatment.effect }}</p>
                  </el-card>
                </el-timeline-item>
              </el-timeline>
            </div>
            <el-empty v-else description="暂无治疗记录"></el-empty>
          </el-tab-pane>

          <!-- 添加药方记录标签页 -->
          <el-tab-pane label="药方记录" name="prescription">
            <div v-if="selectedPatient.prescriptions && selectedPatient.prescriptions.length > 0">
              <div v-for="(prescription, index) in selectedPatient.prescriptions" :key="index"
                class="prescription-item">
                <el-card>
                  <div slot="header" class="prescription-header">
                    <span><strong>药方编号:</strong> {{ prescription.id }}</span>
                    <span><strong>开具时间:</strong> {{ prescription.time }}</span>
                    <span><strong>开方医生:</strong> {{ prescription.doctor }}</span>
                  </div>
                  <el-table :data="prescription.medications" border size="small">
                    <el-table-column prop="name" label="药品名称" width="180"></el-table-column>
                    <el-table-column prop="dosage" label="用量" width="120"></el-table-column>
                    <el-table-column prop="frequency" label="频次" width="120"></el-table-column>
                    <el-table-column prop="duration" label="疗程" width="120"></el-table-column>
                    <el-table-column prop="notes" label="说明"></el-table-column>
                  </el-table>
                  <div class="prescription-notes" v-if="prescription.notes">
                    <p><strong>备注:</strong> {{ prescription.notes }}</p>
                  </div>
                </el-card>
              </div>
            </div>
            <el-empty v-else description="暂无药方记录"></el-empty>
          </el-tab-pane>
        </el-tabs>
      </div>
      <div class="patient-details-placeholder" v-else>
        <el-empty description="请从左侧列表选择患者"></el-empty>
      </div>
    </div>

    <!-- 开检查单弹窗 -->
    <el-dialog title="开具检查单" :visible.sync="dialogExamOrder" width="650px">
      <el-form :model="examOrderForm" label-width="100px" ref="examOrderForm" :rules="examOrderRules">
        <el-form-item label="检查项目" prop="name">
          <el-select v-model="examOrderForm.name" placeholder="请选择检查项目" style="width: 100%">
            <el-option v-for="item in examOptions" :key="item.name" :label="item.label" :value="item.name"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="检查科室" prop="department">
          <el-select v-model="examOrderForm.department" placeholder="请选择检查科室" style="width: 100%">
            <el-option label="检验科" value="检验科"></el-option>
            <el-option label="放射科" value="放射科"></el-option>
            <el-option label="超声科" value="超声科"></el-option>
            <el-option label="心电图室" value="心电图室"></el-option>
            <el-option label="内窥镜室" value="内窥镜室"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="检查说明" prop="description">
          <el-input type="textarea" v-model="examOrderForm.description" rows="3"></el-input>
        </el-form-item>
        <el-form-item label="注意事项">
          <el-input type="textarea" v-model="examOrderForm.notes" rows="2"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogExamOrder = false">取 消</el-button>
        <el-button type="primary" @click="submitExamOrder">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 开药方弹窗 -->
    <el-dialog title="开具药方" :visible.sync="dialogPrescription" width="800px">
      <el-form :model="prescriptionForm" label-width="100px" ref="prescriptionForm">
        <el-form-item label="诊断" prop="diagnosis">
          <el-input v-model="prescriptionForm.diagnosis"></el-input>
        </el-form-item>

        <!-- 药品列表 -->
        <div class="medications-list">
          <h4>药品列表</h4>
          <el-table :data="prescriptionForm.medications" border>
            <el-table-column label="药品名称" width="200">
              <template slot-scope="scope">
                <el-select v-model="scope.row.name" placeholder="选择药品" style="width: 100%">
                  <el-option v-for="med in medicationOptions" :key="med.name" :label="med.label" :value="med.name">
                  </el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="用量" width="120">
              <template slot-scope="scope">
                <el-input v-model="scope.row.dosage" placeholder="如:每次1片"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="频次" width="120">
              <template slot-scope="scope">
                <el-select v-model="scope.row.frequency" placeholder="选择频次" style="width: 100%">
                  <el-option label="每日一次" value="每日一次"></el-option>
                  <el-option label="每日两次" value="每日两次"></el-option>
                  <el-option label="每日三次" value="每日三次"></el-option>
                  <el-option label="需要时使用" value="需要时使用"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="疗程" width="120">
              <template slot-scope="scope">
                <el-input v-model="scope.row.duration" placeholder="如:7天"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="说明">
              <template slot-scope="scope">
                <el-input v-model="scope.row.notes" placeholder="用药说明"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="danger" icon="el-icon-delete" size="small" @click="removeMedication(scope.$index)"
                  circle>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="add-medication">
            <el-button type="primary" icon="el-icon-plus" size="small" @click="addMedication">
              添加药品
            </el-button>
          </div>
        </div>

        <el-form-item label="备注" prop="notes">
          <el-input type="textarea" v-model="prescriptionForm.notes" rows="2"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogPrescription = false">取 消</el-button>
        <el-button type="primary" @click="submitPrescription">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 记录治疗弹窗 -->
    <el-dialog title="记录治疗情况" :visible.sync="dialogTreatment" width="650px">
      <el-form :model="treatmentForm" label-width="100px" ref="treatmentForm">
        <el-form-item label="治疗标题" prop="title">
          <el-input v-model="treatmentForm.title"></el-input>
        </el-form-item>
        <el-form-item label="治疗描述" prop="description">
          <el-input type="textarea" v-model="treatmentForm.description" rows="3"></el-input>
        </el-form-item>
        <el-form-item label="用药情况" prop="medication">
          <el-input type="textarea" v-model="treatmentForm.medication" rows="2"></el-input>
        </el-form-item>
        <el-form-item label="治疗效果" prop="effect">
          <el-input v-model="treatmentForm.effect"></el-input>
        </el-form-item>
        <el-form-item label="重要标记">
          <el-switch v-model="treatmentForm.important"></el-switch>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogTreatment = false">取 消</el-button>
        <el-button type="primary" @click="submitTreatment">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 建议复诊弹窗 -->
    <el-dialog title="建议复诊" :visible.sync="dialogFollowup" width="550px">
      <el-form :model="followupForm" label-width="100px" ref="followupForm">
        <el-form-item label="复诊时间" prop="date">
          <el-date-picker v-model="followupForm.date" type="date" placeholder="选择日期" style="width: 100%">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="复诊原因" prop="reason">
          <el-input type="textarea" v-model="followupForm.reason" rows="3"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFollowup = false">取 消</el-button>
        <el-button type="primary" @click="submitFollowup">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'HospitalReception',
  data() {
    return {
      searchText: '',
      statusFilter: '',
      activeTab: 'registration',
      selectedPatient: null,

      // 各类对话框显示控制
      dialogExamOrder: false,
      dialogPrescription: false,
      dialogTreatment: false,
      dialogFollowup: false,

      // 开检查单表单
      examOrderForm: {
        name: '',
        department: '',
        description: '',
        notes: ''
      },

      // 开检查单表单验证规则
      examOrderRules: {
        name: [{ required: true, message: '请选择检查项目', trigger: 'change' }],
        department: [{ required: true, message: '请选择检查科室', trigger: 'change' }]
      },

      // 检查项目选项
      examOptions: [
        { name: '血常规', label: '血常规', fee: 35 },
        { name: '尿常规', label: '尿常规', fee: 30 },
        { name: '肝功能', label: '肝功能', fee: 60 },
        { name: '肾功能', label: '肾功能', fee: 60 },
        { name: '血糖', label: '血糖', fee: 25 },
        { name: '血脂全套', label: '血脂全套', fee: 90 },
        { name: '甲状腺功能', label: '甲状腺功能', fee: 100 },
        { name: '心电图', label: '心电图', fee: 45 },
        { name: '胸部X光', label: '胸部X光', fee: 80 },
        { name: '腹部B超', label: '腹部B超', fee: 120 },
        { name: '心脏彩超', label: '心脏彩超', fee: 180 },
        { name: '颈动脉超声', label: '颈动脉超声', fee: 150 },
        { name: '头颅CT', label: '头颅CT', fee: 280 },
        { name: '胸部CT', label: '胸部CT', fee: 320 },
        { name: '腹部CT', label: '腹部CT', fee: 350 }
      ],

      // 药方表单
      prescriptionForm: {
        diagnosis: '',
        medications: [{ name: '', dosage: '', frequency: '', duration: '', notes: '' }],
        notes: ''
      },

      // 药品选项
      medicationOptions: [
        { name: '阿莫西林胶囊', label: '阿莫西林胶囊 0.25g*24粒' },
        { name: '盐酸左氧氟沙星片', label: '盐酸左氧氟沙星片 0.1g*12片' },
        { name: '头孢克洛胶囊', label: '头孢克洛胶囊 0.25g*12粒' },
        { name: '阿司匹林肠溶片', label: '阿司匹林肠溶片 100mg*30片' },
        { name: '硝酸甘油片', label: '硝酸甘油片 0.5mg*100片' },
        { name: '盐酸二甲双胍片', label: '盐酸二甲双胍片 0.5g*60片' },
        { name: '蒙脱石散', label: '蒙脱石散 3g*10袋' },
        { name: '布洛芬缓释胶囊', label: '布洛芬缓释胶囊 0.3g*10粒' },
        { name: '复方甘草片', label: '复方甘草片 0.5g*24片' },
        { name: '复方丹参滴丸', label: '复方丹参滴丸 27mg*180丸' },
        { name: '枸橼酸西地那非片', label: '枸橼酸西地那非片 50mg*2片' },
        { name: '酮康唑软膏', label: '酮康唑软膏 10g' }
      ],

      // 治疗记录表单
      treatmentForm: {
        title: '',
        description: '',
        medication: '',
        effect: '',
        important: false
      },

      // 复诊建议表单
      followupForm: {
        date: '',
        reason: ''
      },

      patients: [
        {
          id: 'P001',
          name: '徐浩然',
          gender: '男',
          age: 45,
          phone: '13812345678',
          registerTime: '2025-05-09 08:30',
          status: 'waiting',
          registration: {
            id: 'R20250509001',
            registerTime: '2025-05-09 08:30',
            department: '内科',
            doctor: '李医生',
            fee: 35,
            paid: true,
            complaint: '持续头痛两天，伴有轻微发热，无其他不适'
          },
          examOrders: [],
          examResults: [],
          treatments: [],
          prescriptions: [],
          followupRecommendation: null
        },
        {
          id: 'P002',
          name: '孟白',
          gender: '女',
          age: 32,
          phone: '13987654321',
          registerTime: '2025-05-09 09:15',
          status: 'inProgress',
          registration: {
            id: 'R20250509002',
            registerTime: '2025-05-09 09:15',
            department: '消化内科',
            doctor: '王医生',
            fee: 50,
            paid: true,
            complaint: '腹痛腹泻三天，食欲下降'
          },
          examOrders: [
            {
              id: 'E20250509001',
              name: '血常规',
              department: '检验科',
              doctor: '王医生',
              status: '已完成',
              fee: 35,
              time: '2025-05-09 09:30'
            },
            {
              id: 'E20250509002',
              name: '腹部超声',
              department: '超声科',
              doctor: '王医生',
              status: '进行中',
              fee: 120,
              time: '2025-05-09 09:40'
            }
          ],
          examResults: [
            {
              name: '血常规检查',
              time: '2025-05-09 10:15',
              doctor: '赵技师',
              conclusion: '血常规基本正常，轻度炎症反应',
              description: '白细胞计数略高：10.2×10^9/L（参考范围：4.0-10.0×10^9/L），中性粒细胞百分比：75%，淋巴细胞：20%，单核细胞：4%，嗜酸性粒细胞：1%，嗜碱性粒细胞：0%。红细胞及血小板计数均在正常范围内。',
              images: []
            }
          ],
          treatments: [
            {
              title: '初次诊疗',
              time: '2025-05-09 09:20',
              doctor: '王医生',
              description: '初步诊断为急性肠胃炎，建议进行相关检查',
              medication: '开具药物：蒙脱石散（3盒），益生菌（2盒），每日三次，饭后服用',
              effect: '待观察',
              important: true
            }
          ],
          prescriptions: [
            {
              id: 'P20250509001',
              time: '2025-05-09 09:25',
              doctor: '王医生',
              diagnosis: '急性肠胃炎',
              medications: [
                {
                  name: '蒙脱石散',
                  dosage: '每次1袋',
                  frequency: '每日三次',
                  duration: '3天',
                  notes: '饭后用温水冲服'
                },
                {
                  name: '复合乳酸菌胶囊',
                  dosage: '每次2粒',
                  frequency: '每日三次',
                  duration: '5天',
                  notes: '饭后服用'
                }
              ],
              notes: '注意饮食清淡，多喝水，避免辛辣刺激食物'
            }
          ],
          followupRecommendation: {
            date: '2025-05-16',
            reason: '复查肠胃炎恢复情况，如症状加重请提前就诊'
          }
        },
        {
          id: 'P003',
          name: '王原胜',
          gender: '男',
          age: 67,
          phone: '13765432198',
          registerTime: '2025-05-09 10:00',
          status: 'completed',
          registration: {
            id: 'R20250509003',
            registerTime: '2025-05-09 10:00',
            department: '心内科',
            doctor: '陈医生',
            fee: 60,
            paid: true,
            complaint: '胸闷气短，活动后加重，有高血压病史'
          },
          examOrders: [
            {
              id: 'E20250509003',
              name: '心电图',
              department: '心电图室',
              doctor: '陈医生',
              status: '已完成',
              fee: 45,
              time: '2025-05-09 10:10'
            },
            {
              id: 'E20250509004',
              name: '心脏彩超',
              department: '超声科',
              doctor: '陈医生',
              status: '已完成',
              fee: 180,
              time: '2025-05-09 10:30'
            },
            {
              id: 'E20250509005',
              name: '血生化全套',
              department: '检验科',
              doctor: '陈医生',
              status: '已完成',
              fee: 120,
              time: '2025-05-09 10:15'
            }
          ],
          examResults: [
            {
              name: '心电图检查',
              time: '2025-05-09 10:25',
              doctor: '钱技师',
              conclusion: '窦性心律，心率72次/分钟，T波轻度改变',
              description: '心电图显示窦性心律，心率72次/分钟，未见明显心肌缺血征象，T波在V3-V5导联呈平坦状态，可见轻度ST段改变。',
              images: [
                {
                  url: 'https://via.placeholder.com/400x300?text=心电图图像1',
                  title: '标准12导联'
                }
              ]
            },
            {
              name: '心脏彩超检查',
              time: '2025-05-09 11:00',
              doctor: '孙医生',
              conclusion: '左心室轻度肥厚，射血分数正常',
              description: '心脏彩超检查显示：左心房正常大小，左心室轻度肥厚，室壁运动正常，射血分数60%（正常范围），二尖瓣轻度返流，其余瓣膜未见明显异常。',
              images: [
                {
                  url: 'https://via.placeholder.com/400x300?text=心脏彩超图像1',
                  title: '长轴切面'
                },
                {
                  url: 'https://via.placeholder.com/400x300?text=心脏彩超图像2',
                  title: '四腔心切面'
                }
              ]
            },
            {
              name: '血生化检查',
              time: '2025-05-09 11:30',
              doctor: '周技师',
              conclusion: '血脂轻度异常，肝肾功能正常',
              description: '总胆固醇：5.8mmol/L（参考范围：<5.2mmol/L），低密度脂蛋白：3.6mmol/L（参考范围：<3.4mmol/L），高密度脂蛋白：1.0mmol/L（参考范围：>1.0mmol/L）。肝功能、肾功能、血糖等其他指标在正常范围内。',
              images: []
            }
          ],
          treatments: [
            {
              title: '首次诊疗',
              time: '2025-05-09 10:05',
              doctor: '陈医生',
              description: '初步诊断为冠心病，心功能II级，高血压Ⅱ级',
              medication: '硝酸甘油片（1盒），备用；阿司匹林肠溶片（1盒），每日一次，每次100mg；缬沙坦片（1盒），每日一次，每次80mg',
              effect: '待观察',
              important: true
            },
            {
              title: '复诊评估',
              time: '2025-05-09 11:40',
              doctor: '陈医生',
              description: '根据检查结果，确诊为冠心病，合并高血压，需要长期服药治疗',
              medication: '硝酸异山梨酯片（1盒），每日三次；阿托伐他汀钙片（1盒），每晚一次，每次20mg；美托洛尔缓释片（1盒），每日一次，每次47.5mg',
              effect: '患者症状得到缓解，建议两周后复诊',
              important: false
            }
          ],
          prescriptions: [
            {
              id: 'P20250509002',
              time: '2025-05-09 11:45',
              doctor: '陈医生',
              diagnosis: '冠心病，高血压Ⅱ级',
              medications: [
                {
                  name: '硝酸异山梨酯片',
                  dosage: '每次20mg',
                  frequency: '每日三次',
                  duration: '30天',
                  notes: '餐前服用'
                },
                {
                  name: '阿托伐他汀钙片',
                  dosage: '每次20mg',
                  frequency: '每日一次',
                  duration: '30天',
                  notes: '睡前服用'
                },
                {
                  name: '美托洛尔缓释片',
                  dosage: '每次47.5mg',
                  frequency: '每日一次',
                  duration: '30天',
                  notes: '早餐后服用'
                },
                {
                  name: '阿司匹林肠溶片',
                  dosage: '每次100mg',
                  frequency: '每日一次',
                  duration: '30天',
                  notes: '餐后服用'
                }
              ],
              notes: '定期监测血压，避免剧烈运动，饮食清淡少盐'
            }
          ],
          followupRecommendation: {
            date: '2025-05-23',
            reason: '复查血压控制情况及冠心病症状，需要复查心电图'
          }
        },
        {
          id: 'P004',
          name: '赵三强',
          gender: '女',
          age: 28,
          phone: '13698765432',
          registerTime: '2025-05-09 10:30',
          status: 'waiting',
          registration: {
            id: 'R20250509004',
            registerTime: '2025-05-09 10:30',
            department: '皮肤科',
            doctor: '刘医生',
            fee: 40,
            paid: true,
            complaint: '面部皮疹一周，伴有轻微瘙痒'
          },
          examOrders: [],
          examResults: [],
          treatments: [],
          prescriptions: [],
          followupRecommendation: null
        },
        {
          id: 'P005',
          name: '钱小亮',
          gender: '男',
          age: 52,
          phone: '13576543210',
          registerTime: '2025-05-09 11:00',
          status: 'waiting',
          registration: {
            id: 'R20250509005',
            registerTime: '2025-05-09 11:00',
            department: '骨科',
            doctor: '张医生',
            fee: 50,
            paid: false,
            complaint: '右膝关节疼痛一个月，活动受限'
          },
          examOrders: [],
          examResults: [],
          treatments: [],
          prescriptions: [],
          followupRecommendation: null
        }
      ]
    }
  },
  computed: {
    filteredPatients() {
      return this.patients.filter(patient => {
        const matchSearch = !this.searchText ||
          patient.name.includes(this.searchText) ||
          patient.id.includes(this.searchText);

        const matchStatus = !this.statusFilter ||
          patient.status === this.statusFilter;

        return matchSearch && matchStatus;
      });
    }
  },
  methods: {
    handleSelectPatient(row) {
      this.selectedPatient = row;
      this.activeTab = 'registration';
    },
    handleSearch() {
      // 在实际应用中这里会调用API进行搜索
      console.log('搜索条件:', { text: this.searchText, status: this.statusFilter });
    },
    resetSearch() {
      this.searchText = '';
      this.statusFilter = '';
    },
    startDiagnosis() {
      if (this.selectedPatient) {
        this.selectedPatient.status = 'inProgress';
        this.$message.success(`已开始接诊患者：${this.selectedPatient.name}`);
      }
    },
    completeDiagnosis() {
      if (this.selectedPatient) {
        this.selectedPatient.status = 'completed';
        this.$message.success(`已完成接诊患者：${this.selectedPatient.name}`);
      }
    },
    getStatusType(status) {
      switch (status) {
        case 'waiting': return 'warning';
        case 'inProgress': return 'primary';
        case 'completed': return 'success';
        default: return 'info';
      }
    },
    getStatusText(status) {
      switch (status) {
        case 'waiting': return '待接诊';
        case 'inProgress': return '接诊中';
        case 'completed': return '已完成';
        default: return '未知';
      }
    },
    getExamStatusType(status) {
      switch (status) {
        case '待执行': return 'info';
        case '进行中': return 'primary';
        case '已完成': return 'success';
        default: return 'info';
      }
    },

    // 开检查单
    submitExamOrder() {
      this.$refs.examOrderForm.validate(valid => {
        if (valid) {
          // 获取当前时间作为申请时间
          const now = new Date();
          const timeStr = now.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(/\//g, '-');

          // 生成检查单ID (简单示例)
          const examId = 'E' + now.getFullYear().toString() +
            ('0' + (now.getMonth() + 1)).slice(-2) +
            ('0' + now.getDate()).slice(-2) +
            ('0' + now.getHours()).slice(-2) +
            ('0' + now.getMinutes()).slice(-2);

          // 查找对应的检查项目获取费用
          const examItem = this.examOptions.find(item => item.name === this.examOrderForm.name);
          const fee = examItem ? examItem.fee : 0;

          // 创建新的检查单对象
          const newExamOrder = {
            id: examId,
            name: this.examOrderForm.name,
            department: this.examOrderForm.department,
            doctor: '当前医生', // 实际应用中应该从登录用户获取
            status: '待执行',
            fee: fee,
            time: timeStr,
            description: this.examOrderForm.description,
            notes: this.examOrderForm.notes
          };

          // 添加到患者的检查单列表
          if (!this.selectedPatient.examOrders) {
            this.selectedPatient.examOrders = [];
          }
          this.selectedPatient.examOrders.push(newExamOrder);

          // 成功提示
          this.$message.success('检查单已开具成功');

          // 切换到检查单标签页
          this.activeTab = 'examOrder';

          // 关闭对话框并重置表单
          this.dialogExamOrder = false;
          this.resetExamOrderForm();
        } else {
          this.$message.error('请完善检查单信息');
        }
      });
    },

    resetExamOrderForm() {
      this.examOrderForm = {
        name: '',
        department: '',
        description: '',
        notes: ''
      };
    },

    // 药方相关方法
    addMedication() {
      this.prescriptionForm.medications.push({
        name: '',
        dosage: '',
        frequency: '',
        duration: '',
        notes: ''
      });
    },

    removeMedication(index) {
      this.prescriptionForm.medications.splice(index, 1);
      // 确保至少有一个药品
      if (this.prescriptionForm.medications.length === 0) {
        this.addMedication();
      }
    },

    submitPrescription() {
      // 验证是否有药品
      if (this.prescriptionForm.medications.some(med => !med.name)) {
        this.$message.error('请至少添加一个有效的药品信息');
        return;
      }

      // 获取当前时间
      const now = new Date();
      const timeStr = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-');

      // 生成药方ID
      const presId = 'P' + now.getFullYear().toString() +
        ('0' + (now.getMonth() + 1)).slice(-2) +
        ('0' + now.getDate()).slice(-2) +
        ('0' + now.getHours()).slice(-2) +
        ('0' + now.getMinutes()).slice(-2);

      // 创建新的药方对象
      const newPrescription = {
        id: presId,
        time: timeStr,
        doctor: '当前医生', // 实际应用中应该从登录用户获取
        diagnosis: this.prescriptionForm.diagnosis,
        medications: JSON.parse(JSON.stringify(this.prescriptionForm.medications)), // 深拷贝避免引用问题
        notes: this.prescriptionForm.notes
      };

      // 添加到患者的药方列表
      if (!this.selectedPatient.prescriptions) {
        this.selectedPatient.prescriptions = [];
      }
      this.selectedPatient.prescriptions.push(newPrescription);

      // 成功提示
      this.$message.success('药方已开具成功');

      // 切换到药方标签页
      this.activeTab = 'prescription';

      // 关闭对话框并重置表单
      this.dialogPrescription = false;
      this.resetPrescriptionForm();
    },

    resetPrescriptionForm() {
      this.prescriptionForm = {
        diagnosis: '',
        medications: [{ name: '', dosage: '', frequency: '', duration: '', notes: '' }],
        notes: ''
      };
    },

    // 治疗记录相关方法
    submitTreatment() {
      if (!this.treatmentForm.title || !this.treatmentForm.description) {
        this.$message.error('请填写治疗标题和描述');
        return;
      }

      // 获取当前时间
      const now = new Date();
      const timeStr = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-');

      // 创建新的治疗记录对象
      const newTreatment = {
        title: this.treatmentForm.title,
        time: timeStr,
        doctor: '当前医生', // 实际应用中应该从登录用户获取
        description: this.treatmentForm.description,
        medication: this.treatmentForm.medication,
        effect: this.treatmentForm.effect,
        important: this.treatmentForm.important
      };

      // 添加到患者的治疗记录列表
      if (!this.selectedPatient.treatments) {
        this.selectedPatient.treatments = [];
      }
      this.selectedPatient.treatments.push(newTreatment);

      // 成功提示
      this.$message.success('治疗记录已添加成功');

      // 切换到治疗标签页
      this.activeTab = 'treatment';

      // 关闭对话框并重置表单
      this.dialogTreatment = false;
      this.resetTreatmentForm();
    },

    resetTreatmentForm() {
      this.treatmentForm = {
        title: '',
        description: '',
        medication: '',
        effect: '',
        important: false
      };
    },

    // 建议复诊相关方法
    submitFollowup() {
      if (!this.followupForm.date || !this.followupForm.reason) {
        this.$message.error('请选择复诊日期并填写复诊原因');
        return;
      }

      // 格式化日期
      let dateStr = '';
      if (this.followupForm.date instanceof Date) {
        dateStr = this.followupForm.date.toLocaleDateString('zh-CN');
      } else {
        dateStr = this.followupForm.date;
      }

      // 创建复诊建议对象
      const followupRecommendation = {
        date: dateStr,
        reason: this.followupForm.reason
      };

      // 添加到患者信息
      this.selectedPatient.followupRecommendation = followupRecommendation;

      // 成功提示
      this.$message.success('已添加复诊建议');

      // 切换到挂号信息标签页，可以查看复诊建议
      this.activeTab = 'registration';

      // 关闭对话框并重置表单
      this.dialogFollowup = false;
      this.resetFollowupForm();
    },

    resetFollowupForm() {
      this.followupForm = {
        date: '',
        reason: ''
      };
    }
  }
}
</script>

<style scoped>
.hospital-reception {
  font-family: Arial, sans-serif;
  padding: 20px;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #409EFF;
}

.filter-area {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.main-content {
  display: flex;
  min-height: 600px;
}

.patient-list {
  width: 40%;
  margin-right: 20px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 10px;
}

.patient-list h3 {
  padding: 0 0 10px;
  margin: 0;
  border-bottom: 1px solid #EBEEF5;
}

.patient-details,
.patient-details-placeholder {
  width: 60%;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 15px;
}

.patient-header {
  margin-bottom: 20px;
}

.patient-info {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  margin-bottom: 15px;
}

.patient-info span {
  margin-right: 20px;
  margin-bottom: 10px;
}

.action-buttons {
  margin-bottom: 15px;
}

.exam-result-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
}

.exam-result-item h4 {
  margin-top: 0;
  border-bottom: 1px solid #EBEEF5;
  padding-bottom: 10px;
}

.result-content {
  display: flex;
}

.result-text {
  flex: 1;
}

.result-images {
  flex: 1;
  margin-left: 20px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
}

.image-item {
  width: 150px;
  margin: 5px;
  text-align: center;
}

.image-item .el-image {
  width: 140px;
  height: 100px;
  border: 1px solid #EBEEF5;
}

.image-title {
  font-size: 12px;
  margin-top: 5px;
}

/* 新增样式 */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.prescription-item {
  margin-bottom: 20px;
}

.prescription-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.prescription-notes {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #EBEEF5;
}

.medications-list {
  margin-bottom: 20px;
}

.add-medication {
  margin-top: 10px;
  text-align: center;
}

.followup-recommendation {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #EBEEF5;
}

.followup-recommendation h4 {
  margin-bottom: 10px;
  color: #409EFF;
}
</style>